/**
 * Illuminate 域 — API 接口
 * 
 * 对接后端 health-profile / reports / health-score / metric-analysis
 */

import { http } from '../../api/http';
import { API_BASE } from '../../api/config';
import { createSSEStream } from '../../api/sse';
import { useSessionStore } from '../../stores/session-store';

// ── 类型定义 ──

export interface HealthMetric {
  key: string;
  label: string;
  value: number;
  unit: string;
  trend: number;       // -1 ~ 1, 正数表示上升
  status: 'normal' | 'high' | 'low';
  refRange?: string;   // 参考范围字符串，如 "0.56-1.7"
  updatedAt?: string;
}

export interface HealthScore {
  score: number;       // mapped from overallScore
  label: string;       // mapped from levelLabel
  overallScore: number;
  levelLabel: string;
  level: string;
  totalIndicators: number;
  totalAbnormal: number;
  dimensions: { name: string; score: number; max: number }[];
}

export interface DailyTip {
  content: string;
  source?: string;
}

export interface Report {
  taskId: string;
  fileName: string;
  fileType: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progressPercent: number;
  extractedMetricsCount: number;
  scoreContribution: number;
  createdAt: string;
}

export interface ReportDetail {
  id: string;
  title: string;
  date: string;
  indicators: HealthMetric[];
  summary?: string;
}

export interface MetricAnalysis {
  metric: string;
  interpretation: string;
  riskLevel: 'low' | 'medium' | 'high';
  suggestions: string[];
  references?: string[];
}

/** 维度矩阵中的单个指标 */
export interface DimensionIndicator {
  name: string;
  loincCode: string;
  value: string;
  unit: string;
  status: 'normal' | 'low' | 'high';
  refRange: string;
}

/** 维度矩阵中的单个维度 */
export interface DimensionItem {
  key: string;
  name: string;
  label: string;        // 后端返回 label（中文名）
  score: number;        // 由前端从 indicators 计算
  icon?: string;
  indicators: DimensionIndicator[];
}

/** AI 健康洞察 */
export interface HealthInsight {
  id: string;
  content: string;
  generatedAt: string;
  cached: boolean;
}

/** 生理年龄 — 单系统下的 biomarker */
export interface SystemBiomarker {
  key: string;        // loincCode 或 name
  name: string;
  value: string;
  unit: string;
  status: 'normal' | 'high' | 'low';
  refRange?: string;
}

/** 生理年龄评估 */
export interface SystemAge {
  key: string;
  name: string;
  age: number;
  status: 'better' | 'same' | 'worse';
  note?: string;
  confidenceRange?: string;
  indicators?: SystemBiomarker[];
}

export interface BiologicalAgeImprovementTarget {
  key: string;
  name: string;
  value: string;
  unit: string;
  status: 'high' | 'low';
  refRange?: string;
  direction: 'lower' | 'raise' | 'optimize';
  target?: string;
  reason: string;
  systemKey?: string;
  systemName?: string;
}

export interface BiologicalAge {
  overallAge: number;
  chronologicalAge?: number;
  systemAges: SystemAge[];
  improvementTargets?: BiologicalAgeImprovementTarget[];
  reasoning: string;
  recommendations: string[];
  confidenceRange?: string;
  generatedAt: string;
  cached: boolean;
  stale?: boolean;
  refreshing?: boolean;
}

/** 神经游戏结果 */
export interface NeuroGameResult {
  id: string;
  reactionTimeAvgMs?: number;
  reactionTimeBestMs?: number;
  digitSpanMax?: number;
  testedAt: string;
}

export interface SaveNeuroGameResultPayload {
  reactionTimeAvgMs?: number;
  reactionTimeBestMs?: number;
  digitSpanMax?: number;
}

/** 指标历史数据点 */
export interface IndicatorDataPoint {
  date: string;
  value: number;
  unit: string;
  status: 'normal' | 'low' | 'high';
}

/** 指标历史趋势 */
export interface IndicatorHistory {
  name: string;
  dataPoints: IndicatorDataPoint[];
  trend: 'rising' | 'falling' | 'stable';
  changeRate: number;
  interpretation: string;
  totalRecords: number;
  refRange?: { low: number; high: number };
}

/** 关注的指标 */
export interface WatchedIndicator {
  name: string;
  displayName: string;
  loincCode?: string;
  addedAt: string;
}

/** 系统推荐指标 */
export interface RecommendedIndicator {
  indicatorName: string;
  loincCode?: string;
  reason: string;
  sourceType: 'fhir_abnormal' | 'ocr_finding' | 'bio_age_weak' | 'llm_recommended';
  status?: 'normal' | 'high' | 'low';
  latestValue?: number;
  unit?: string;
  observedAt?: string;
}

/** 关注指标及其最新记录 */
export interface WatchedMetric {
  key: string;
  label: string;
  value: number | null;
  unit: string;
  status: 'normal' | 'high' | 'low';
  updatedAt: string | null;
  hasData: boolean;
}

/** 指标原始记录 */
export interface ObservationRecord {
  id: string;
  value: number | null;
  unit: string;
  date: string;
  status: 'normal' | 'low' | 'high';
  source: string;
}

/** AI 报告解读 */
export interface AnalysisInterpretation {
  interpretationId: string;
  taskId: string;
  content: string;
  modelVersion?: string;
  createdAt: string;
}

// ── API 调用 ──

export const illuminateApi = {
  /** 获取用户健康评分 */
  async getHealthScore(): Promise<HealthScore> {
    const raw = await http.get<{
      overallScore: number; levelLabel: string; level: string;
      totalIndicators: number; totalAbnormal: number; lastUpdated: string;
      dimensions: Array<{ key: string; label: string; score?: number; normalCount: number; totalCount: number }>;
    }>('/health/score');
    return {
      ...raw,
      score: raw.overallScore,
      label: raw.levelLabel,
      dimensions: (raw.dimensions ?? []).map(d => ({
        name: d.label,
        score: d.score ?? (d.totalCount > 0 ? Math.round(d.normalCount / d.totalCount * 100) : 0),
        max: 100,
      })),
    };
  },

  /** 获取每日健康提示 */
  async getDailyTip(): Promise<DailyTip> {
    return http.get<DailyTip>('/health/tip');
  },

  /** 获取用户健康档案（含关注指标） */
  async getHealthProfile(): Promise<{ metrics: HealthMetric[] }> {
    return http.get('/health-profile');
  },

  /** 获取健康数据（综合）— 后端返回 flat 数组，适配为 { metrics } 格式 */
  async getHealthData(): Promise<{ metrics: HealthMetric[] }> {
    const raw = await http.get<Array<{
      id: string; type: string; loincCode?: string; value: number; unit: string;
      date: string; createdAt: string; refRange?: string; status?: string;
    }>>('/health-data?limit=200');
    // items 按 createdAt DESC 返回，同类型第一次出现 = 最新，第二次 = 上一次
    const items = Array.isArray(raw) ? raw : [];
    const byKey = new Map<string, { latest: typeof items[0]; prev?: typeof items[0] }>();
    for (const item of items) {
      // Use loincCode as dedup key, fall back to type name
      const key = item.loincCode ?? item.type;
      const entry = byKey.get(key);
      if (!entry) {
        byKey.set(key, { latest: item });
      } else if (!entry.prev) {
        byKey.set(key, { ...entry, prev: item });
      }
    }
    const metrics: HealthMetric[] = Array.from(byKey.values())
      .filter(({ latest }) => latest.value != null && latest.value !== 0)
      .map(({ latest, prev }) => {
        const loincCode = latest.loincCode ?? '';
        const trend = (prev && prev.value && prev.value !== 0)
          ? (latest.value - prev.value) / Math.abs(prev.value)
          : 0;
        const status = (latest.status === 'high' || latest.status === 'low')
          ? latest.status
          : 'normal';
        return {
          key: loincCode || latest.type,
          label: latest.type,
          value: latest.value,
          unit: latest.unit ?? '',
          trend,
          status,
          refRange: latest.refRange,
          updatedAt: latest.date,
        };
      });
    return { metrics };
  },

  /** 同步前端 Apple Health 数据到后端 */
  async syncAppleHealthMetrics(metrics: HealthMetric[]): Promise<{ syncedCount: number }> {
    return http.post<{ syncedCount: number }>('/health-data/sync', metrics);
  },


  /** 获取报告列表 */
  async getReports(): Promise<Report[]> {
    const res = await http.get<Report[]>('/reports/list');
    return Array.isArray(res) ? res : [];
  },

  /** 获取报告详情 */
  async getReportDetail(taskId: string): Promise<ReportDetail> {
    return http.get<ReportDetail>(`/reports/${taskId}/result`);
  },

  /**
   * 单指标 AI 解读 — SSE 流式，逐块 yield 文本
   * 调用方用 for-await-of 消费并实时更新 UI
   */
  async *analyzeMetric(
    metric: string,
    value: number,
    unit: string,
    history?: { date: string; value: number; unit: string }[],
    signal?: AbortSignal,
  ): AsyncGenerator<string> {
    const stream = createSSEStream('/metric-analysis', {
      body: { metricName: metric, value, unit, history },
      getToken: () => useSessionStore.getState().token,
      signal,
    });
    for await (const event of stream) {
      try {
        const parsed = JSON.parse(event.data) as { type: string; text?: string };
        if (parsed.type === 'content' && parsed.text) yield parsed.text;
        if (parsed.type === 'done') return;
      } catch { /* skip */ }
    }
  },

  /** 深度 AI 报告解读 */
  async *analyzeReport(taskId: string, signal?: AbortSignal): AsyncGenerator<string> {
    const stream = createSSEStream(`/reports/${taskId}/analysis`, {
      method: 'POST',
      getToken: () => useSessionStore.getState().token,
      signal,
    });

    for await (const event of stream) {
      try {
        const parsed = JSON.parse(event.data) as { type: string; text?: string };
        if (parsed.type === 'content' && parsed.text) yield parsed.text;
        if (parsed.type === 'done') return;
      } catch (err) { console.error('SSE Parse Error:', err, event); }
    }
  },

  /** 8+N 维度矩阵 — 后端维度无 score，从 indicators 计算 */
  async getDimensionMatrix(): Promise<{ dimensions: DimensionItem[] }> {
    const raw = await http.get<{
      dimensions: Array<{
        key: string; label: string; icon?: string;
        indicators: Array<{
          name: string; loincCode?: string; value: number; unit: string;
          status: string; referenceRange: string | null;
        }>;
      }>;
    }>('/dimension-matrix');
    const dimensions: DimensionItem[] = (raw.dimensions ?? []).map(d => {
      const normal = d.indicators.filter(i => i.status === 'normal').length;
      const total = d.indicators.length;
      const score = total > 0 ? Math.round(normal / total * 100) : 0;
      return {
        key: d.key,
        name: d.label,
        label: d.label,
        icon: d.icon,
        score,
        indicators: d.indicators.map(i => ({
          name: i.name,
          loincCode: i.loincCode ?? '',
          value: String(i.value),
          unit: i.unit,
          status: (['normal', 'low', 'high'].includes(i.status) ? i.status : 'normal') as 'normal' | 'low' | 'high',
          refRange: i.referenceRange ?? '',
        })),
      };
    });
    return { dimensions };
  },

  /** AI 健康洞察 */
  async getInsights(): Promise<HealthInsight> {
    return http.get<HealthInsight>('/health/insights');
  },

  /** 生理年龄 LLM 评估 */
  async getBiologicalAge(): Promise<BiologicalAge> {
    return http.get<BiologicalAge>('/health/biological-age');
  },

  /** 保存神经游戏结果 */
  async saveNeuroGameResult(payload: SaveNeuroGameResultPayload): Promise<NeuroGameResult> {
    return http.post<NeuroGameResult>('/health/neuro-game-result', payload);
  },

  /** 获取最新神经游戏结果 */
  async getLatestNeuroGameResult(): Promise<NeuroGameResult | null> {
    return http.get<NeuroGameResult | null>('/health/neuro-game-result/latest');
  },

  /** 指标历史趋势（传入 LOINC code） */
  async getIndicatorHistory(loincCode: string, limit = 50, since?: string): Promise<IndicatorHistory> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (since) params.set('since', since);
    return http.get<IndicatorHistory>(`/health/indicators/${encodeURIComponent(loincCode)}/history?${params}`);
  },

  /** 关注指标列表 */
  async getWatchedIndicators(): Promise<{ indicators: WatchedIndicator[] }> {
    const res = await http.get<{
      list: Array<{ indicatorName: string; loincCode?: string; addedAt: string }>;
    }>('/indicators/watched');
    return {
      indicators: (res.list ?? []).map(i => ({
        name: i.indicatorName,
        displayName: i.indicatorName,
        loincCode: i.loincCode,
        addedAt: i.addedAt,
      })),
    };
  },

  /** 关注指标及其最新记录 */
  async getWatchedMetrics(): Promise<{ metrics: WatchedMetric[] }> {
    return http.get<{ metrics: WatchedMetric[] }>('/health/watched-metrics');
  },

  /** 添加关注指标
   * @param name 中文指标名
   * @param loincCode LOINC code（优先传此值，后端用它作为唯一标识）
   * @param source 来源：manual 或 system_recommended
   */
  async addWatchedIndicator(name: string, loincCode?: string, source?: string): Promise<void> {
    await http.post('/indicators/watched', { indicatorName: name, loincCode, source });
  },

  /** 取消关注指标
   * @param identifier LOINC code（优先）或中文指标名
   */
  async removeWatchedIndicator(identifier: string): Promise<void> {
    await http.delete(`/indicators/watched/${encodeURIComponent(identifier)}`);
  },

  /** 获取指标原始记录列表 */
  async getIndicatorObservations(loincCode: string): Promise<{ observations: ObservationRecord[] }> {
    return http.get(`/health/indicators/${encodeURIComponent(loincCode)}/observations`);
  },

  /** 更新单条 Observation 数值 */
  async updateObservation(id: string, value: number, unit: string): Promise<void> {
    await http.put(`/health-profile/observation/${encodeURIComponent(id)}`, { value, unit });
  },

  /** 删除单条 Observation */
  async deleteObservation(id: string): Promise<void> {
    await http.delete(`/health-profile/observation/${encodeURIComponent(id)}`);
  },

  /** 系统推荐指标 */
  async getRecommendedIndicators(): Promise<{ recommendations: RecommendedIndicator[] }> {
    const res = await http.get<{
      recommendations: RecommendedIndicator[];
    }>('/indicators/recommended');
    return { recommendations: res.recommendations ?? [] };
  },

  /** 忽略推荐指标 */
  async dismissRecommendedIndicator(name: string, loincCode?: string): Promise<void> {
    await http.post('/indicators/recommended/dismiss', { indicatorName: name, loincCode });
  },

  /** 删除体检报告 */
  async deleteReport(taskId: string): Promise<void> {
    await http.delete(`/reports/${encodeURIComponent(taskId)}`);
  },

  /** 重新识别体检报告 */
  async reparseReport(taskId: string): Promise<void> {
    await http.post(`/reports/${encodeURIComponent(taskId)}/reparse`);
  },

  /** 保存 AI 报告解读结果 */
  async saveAnalysis(taskId: string, payload: { content: string; modelVersion?: string }): Promise<AnalysisInterpretation> {
    return http.post(`/reports/${encodeURIComponent(taskId)}/analysis/save`, payload);
  },

  /** 获取最新 AI 报告解读 */
  async getLatestAnalysis(taskId: string): Promise<AnalysisInterpretation | null> {
    try {
      return await http.get(`/reports/${encodeURIComponent(taskId)}/analysis/latest`);
    } catch (err: any) {
      if (err?.status === 404 || err?.code === 'NotFound' || err?.code === 'HTTP_404') {
        return null;
      }
      throw err;
    }
  },

  /** 获取 AI 报告解读历史 */
  async getAnalysisHistory(taskId: string): Promise<{
    list: Array<Pick<AnalysisInterpretation, 'interpretationId' | 'content' | 'modelVersion' | 'createdAt'>>;
    total: number;
  }> {
    return http.get(`/reports/${encodeURIComponent(taskId)}/analysis/history`);
  },

  /** 上传体检报告（multipart/form-data，单文件） */
  async uploadReport(file: File): Promise<{
    taskId: string;
    attachmentId: string;
    fileName: string;
    fileType: string;
    saveMode: string;
    parseStatus: string;
    message: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('originalFileName', file.name);

    const token = useSessionStore.getState().token;
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/reports/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!res.ok) {
      const json = await res.json().catch(() => null);
      const message = json?.error?.message ?? json?.message ?? `上传失败 (${res.status})`;
      throw new Error(message);
    }

    const json = await res.json();
    return json.data ?? json;
  },

  /** 上传多份体检报告（multipart/form-data，多文件合并为一个任务） */
  async uploadReports(files: File[]): Promise<{
    taskId: string;
    attachmentIds: string[];
    fileName: string;
    fileType: string;
    saveMode: string;
    parseStatus: string;
    message: string;
  }> {
    const formData = new FormData();
    files.forEach((f) => {
      formData.append('file', f);
      formData.append('originalFileName', f.name);
    });

    const token = useSessionStore.getState().token;
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/reports/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!res.ok) {
      const json = await res.json().catch(() => null);
      const message = json?.error?.message ?? json?.message ?? `上传失败 (${res.status})`;
      throw new Error(message);
    }

    const json = await res.json();
    return json.data ?? json;
  },
};
